import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconArt } from '@/components/generals/icon-art'
import { Scallop } from '@/components/generals/scallop'
import { SectionHeading } from '@/components/generals/section-heading'
import { STEPS } from '@/constants/home'

export const Process: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const [autoplay, setAutoplay] = useState(true)
    const [paused, setPaused] = useState(false)
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

    // Steps play through on their own until someone picks one.
    const choose = (index: number): void => {
        setAutoplay(false)
        setActive(index)
    }

    const advance = (): void => {
        setActive((current) => (current + 1) % STEPS.length)
    }

    const focusTab = (index: number): void => {
        const next = (index + STEPS.length) % STEPS.length
        choose(next)
        tabRefs.current[next]?.focus()
    }

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault()
            focusTab(index + 1)
        }
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault()
            focusTab(index - 1)
        }
        else if (event.key === 'Home') {
            event.preventDefault()
            focusTab(0)
        }
        else if (event.key === 'End') {
            event.preventDefault()
            focusTab(STEPS.length - 1)
        }
    }

    const step = STEPS[active]

    return (
        <section id='process' aria-labelledby='process-title' className='relative bg-butter-soft py-24 sm:py-32'>
            <Scallop className='text-butter-soft' />

            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <SectionHeading
                    id='process-title'
                    lines={['How we get', 'you there']}
                    sticker='Process'
                    stickerShape='gear'
                    stickerColor='text-peach'
                />

                <div
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocusCapture={() => setPaused(true)}
                    onBlurCapture={() => setPaused(false)}
                    className='reveal mt-16 grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12'
                >
                    <div
                        role='tablist'
                        aria-label='Our four steps'
                        aria-orientation='vertical'
                        className='flex flex-col gap-3'
                    >
                        {STEPS.map((item, index) => {
                            const selected = index === active

                            return (
                                <button
                                    key={item.title}
                                    ref={(node) => {
                                        tabRefs.current[index] = node
                                    }}
                                    id={`step-tab-${index}`}
                                    type='button'
                                    role='tab'
                                    aria-selected={selected}
                                    aria-controls='step-panel'
                                    tabIndex={selected ? 0 : -1}
                                    onClick={() => choose(index)}
                                    onKeyDown={(event) => onKeyDown(event, index)}
                                    className={cn(
                                        'group relative flex items-center gap-4 overflow-hidden rounded-[1.5rem] border-2 px-4 py-3 text-left transition-all duration-300 sm:px-5 sm:py-4',
                                        selected
                                            ? 'translate-x-2 border-plum bg-cream shadow-[0_5px_0_0_var(--color-plum)]'
                                            : 'border-transparent hover:translate-x-1 hover:border-plum/30 hover:bg-cream/60'
                                    )}
                                >
                                    {selected && autoplay && (
                                        <span
                                            key={`progress-${active}`}
                                            aria-hidden='true'
                                            onAnimationEnd={advance}
                                            style={{ animationPlayState: paused ? 'paused' : 'running' }}
                                            className='absolute inset-x-0 bottom-0 h-1 origin-left bg-coral animate-[progress-fill_5s_linear_forwards]'
                                        />
                                    )}

                                    <span
                                        className={cn(
                                            'grid size-12 shrink-0 place-items-center rounded-full border-2 border-plum font-heading text-2xl text-plum transition-all duration-300 group-hover:-rotate-12',
                                            selected ? 'scale-110 bg-butter' : 'bg-cream'
                                        )}
                                    >
                                        {index + 1}
                                    </span>

                                    <span className='min-w-0'>
                                        <span className='block font-heading text-[1.6rem] leading-none text-plum'>
                                            {item.title}
                                        </span>

                                        <span className='mt-1 block text-sm font-medium text-plum/65'>
                                            {item.when}
                                        </span>
                                    </span>
                                </button>
                            )
                        })}
                    </div>

                    <div
                        id='step-panel'
                        role='tabpanel'
                        aria-labelledby={`step-tab-${active}`}
                        tabIndex={0}
                        className={cn('relative rounded-[2.5rem] border-2 border-plum p-7 shadow-[0_6px_0_0_var(--color-plum)] sm:p-10', step.color)}
                    >
                        <div key={active} className='grid gap-8 animate-in fade-in slide-in-from-bottom-3 duration-500 sm:grid-cols-[1fr_auto] sm:items-start'>
                            <div className='order-2 sm:order-1'>
                                <p className='font-heading text-lg tracking-wide text-plum/70'>
                                    {`Step ${active + 1} of ${STEPS.length}`}
                                </p>

                                <h3 className='mt-2 font-heading text-[clamp(2.25rem,4.5vw,3.5rem)] leading-[0.95] text-plum'>
                                    {step.title}
                                </h3>

                                <p className='mt-5 text-lg leading-relaxed text-plum/85'>
                                    {step.text}
                                </p>

                                <p className='mt-7 font-heading text-lg tracking-wide text-plum'>
                                    What you get
                                </p>

                                <ul className='mt-3 space-y-2'>
                                    {step.deliverables.map((deliverable) => (
                                        <li key={deliverable} className='flex items-center gap-3 font-medium text-plum'>
                                            <span className='grid size-6 shrink-0 place-items-center rounded-full bg-plum text-cream'>
                                                <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                            </span>
                                            {deliverable}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <IconArt
                                icon={step.icon}
                                shape={step.shape}
                                colorClassName='text-cream'
                                accentClassName={step.accent}
                                tilt={active % 2 === 0 ? 'left' : 'right'}
                                className='order-1 size-36 sm:order-2 sm:size-48'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
